
import { MeetingNote } from "../types";

export async function fetchDriveItems(accessToken: string, days: number = 14): Promise<MeetingNote[]> {
  const lookbackDate = new Date();
  lookbackDate.setDate(lookbackDate.getDate() - days);
  const timeString = lookbackDate.toISOString();

  /**
   * Refined Query:
   * 1. Explicitly exclude trashed items first.
   * 2. Filter by MimeType (Docs or Sheets).
   * 3. Filter by modified time.
   */
  const query = `trashed = false and (mimeType = 'application/vnd.google-apps.document' or mimeType = 'application/vnd.google-apps.spreadsheet') and modifiedTime > '${timeString}'`;
  
  // Requesting the 'trashed' field explicitly to verify it in the loop
  const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id, name, mimeType, modifiedTime, webViewLink, trashed)&pageSize=25`;

  const response = await fetch(searchUrl, {
    headers: { Authorization: `Bearer ${accessToken}` }
  });

  if (!response.ok) {
    const err = await response.text();
    throw new Error(`Drive Search Error: ${err}`);
  }

  const data = await response.json();
  if (!data.files) return [];

  const items: MeetingNote[] = [];

  for (const file of data.files) {
    // Double-check 'trashed' status even if the query should have filtered it
    if (file.trashed) continue;

    try {
      let content = "";
      const source = file.mimeType === 'application/vnd.google-apps.document' ? 'gdoc' : 'gsheet';

      if (source === 'gdoc') {
        const exportUrl = `https://www.googleapis.com/drive/v3/files/${file.id}/export?mimeType=text/plain`;
        const exportRes = await fetch(exportUrl, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (exportRes.ok) content = await exportRes.text();
      } else {
        const sheetsUrl = `https://sheets.googleapis.com/v4/spreadsheets/${file.id}/values/A1:Z50`;
        const sheetRes = await fetch(sheetsUrl, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (sheetRes.ok) {
          const sheetData = await sheetRes.json();
          content = sheetData.values ? sheetData.values.map((row: any[]) => row.join(" | ")).join("\n") : "Empty sheet";
        }
      }

      items.push({
        id: file.id,
        title: file.name,
        date: new Date(file.modifiedTime).toISOString().split('T')[0],
        content: content.slice(0, 5000), // Increased context limit for intelligence report
        source: source as any,
        url: file.webViewLink
      });
    } catch (e) {
      console.warn(`Failed to process Drive file ${file.name}`, e);
    }
  }

  return items;
}
